import React, { useMemo } from 'react';

import { createStyles, Theme, WithStyles, withStyles } from '@material-ui/core';
import MuiButton, { ButtonProps as MuiButtonProps } from '@material-ui/core/Button';
import { fade } from '@material-ui/core/styles/colorManipulator';
import { KeyboardArrowDown as KeyboardArrowDownIcon } from '@material-ui/icons';
import classnames from 'classnames';
import { get } from 'lodash';

const styles = (theme: Theme) =>
  createStyles({
    withIcon: {
      '& svg': {
        marginRight: theme.spacing.unit * 1,
      },
    },
    variantSelect: {
      height: 40,
    },
    sizeExtraSmall: {
      fontSize: theme.typography.pxToRem(9),
    },
    sizeLarge: {
      height: 40,
    },
    sizeExtraLarge: {
      padding: `${theme.spacing.unit * 3.5}px 24px`,
      fontSize: theme.typography.pxToRem(16),
      borderRadius: 15,
      '& span.material-icons': {
        position: 'absolute',
        left: theme.spacing.unit * 3,
        fontSize: theme.spacing.unit * 5,
      },
    },
    /* Default background applied to the root element if `variant="outlined"`. */
    outlined: {
      backgroundColor: theme.palette.common.white,
    },
    /* Styles applied to the root element if `variant="text"` and `color="tertiary"`. */
    textTertiary: {
      color: get(theme.palette, 'tertiary.main'),
      '&:hover': {
        backgroundColor: fade(get(theme.palette, 'tertiary.main'), theme.palette.action.hoverOpacity),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: 'transparent',
        },
      },
    },
    /* Styles applied to the root element if `variant="contained"` and `color="tertiary"`. */
    containedTertiary: {
      color: get(theme.palette, 'tertiary.contrastText'),
      backgroundColor: get(theme.palette, 'tertiary.main'),
      '&:hover': {
        backgroundColor: get(theme.palette, 'tertiary.dark'),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: get(theme.palette, 'tertiary.main'),
        },
      },
    },
    /* Styles applied to the root element if `variant="outlined"` and `color="tertiary"`. */
    outlinedTertiary: {
      color: get(theme.palette, 'tertiary.main'),
      border: `1px solid ${fade(get(theme.palette, 'tertiary.main'), 0.5)}`,
      '&:hover': {
        border: `1px solid ${get(theme.palette, 'tertiary.main')}`,
        backgroundColor: fade(get(theme.palette, 'tertiary.main'), theme.palette.action.hoverOpacity),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: 'transparent',
        },
      },
    },
    /* Styles applied to the root element if `variant="text"` and `color="error"`. */
    textError: {
      color: theme.palette.error.main,
      '&:hover': {
        backgroundColor: fade(theme.palette.error.main, theme.palette.action.hoverOpacity),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: 'transparent',
        },
      },
    },
    /* Styles applied to the root element if `variant="outlined"` and `color="error"`. */
    outlinedError: {
      color: theme.palette.error.main,
      border: `1px solid ${fade(theme.palette.error.main, 0.5)}`,
      '&:hover': {
        border: `1px solid ${theme.palette.error.main}`,
        backgroundColor: fade(theme.palette.error.main, theme.palette.action.hoverOpacity),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: 'transparent',
        },
      },
    },
    /* Styles applied to the root element if `variant="contained"` and `color="error"`. */
    containedError: {
      color: theme.palette.error.contrastText,
      backgroundColor: theme.palette.error.main,
      '&:hover': {
        backgroundColor: theme.palette.error.dark,
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: theme.palette.error.main,
        },
      },
    },
    /* Styles applied to the root element if `variant="text"` and `color="success"`. */
    textSuccess: {
      color: theme.custom.palette.colorSuccess,
      '&:hover': {
        backgroundColor: fade(theme.custom.palette.colorSuccess, theme.palette.action.hoverOpacity),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: 'transparent',
        },
      },
    },
    /* Styles applied to the root element if `variant="outlined"` and `color="success"`. */
    outlinedSuccess: {
      color: theme.custom.palette.colorSuccess,
      border: `1px solid ${fade(theme.custom.palette.colorSuccess, 0.5)}`,
      '&:hover': {
        border: `1px solid ${theme.custom.palette.colorSuccess}`,
        backgroundColor: fade(theme.custom.palette.colorSuccess, theme.palette.action.hoverOpacity),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: 'transparent',
        },
      },
    },
    /* Styles applied to the root element if `variant="contained"` and `color="success"`. */
    containedSuccess: {
      color: theme.palette.error.contrastText,
      backgroundColor: theme.custom.palette.colorSuccess,
      '&:hover': {
        backgroundColor: theme.custom.palette.colorSuccessHover,
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: theme.custom.palette.colorSuccess,
        },
      },
    },
    /* Styles applied to the root element if `variant="text"` and `color="lightBlue"`. */
    textLightBlue: {
      color: theme.custom.palette.colorSupportSteel,
      '&:hover': {
        backgroundColor: fade(theme.custom.palette.colorLightBlue, theme.palette.action.hoverOpacity),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: 'transparent',
        },
      },
    },
    /* Styles applied to the root element if `variant="outlined"` and `color="lightBlue"`. */
    outlinedLightBlue: {
      color: theme.custom.palette.colorSupportSteel,
      border: `1px solid ${theme.custom.palette.colorLightBlue}`,
      '&:hover': {
        border: `1px solid ${theme.custom.palette.colorLightBlue}`,
        backgroundColor: fade(theme.custom.palette.colorLightBlue, theme.palette.action.hoverOpacity),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: 'transparent',
        },
      },
    },
    /* Styles applied to the root element if `variant="contained"` and `color="lightBlue"`. */
    containedLightBlue: {
      color: theme.custom.palette.colorSupportSteel,
      border: `1px solid ${theme.custom.palette.colorLightBlue}`,
      backgroundColor: theme.custom.palette.colorLightBlue,
      boxShadow: 'none !important',
      '&:hover': {
        backgroundColor: theme.custom.palette.colorLightBlue,
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: theme.custom.palette.colorLightBlue,
        },
      },
    },
    /* Styles applied to the root element if `variant="text"` and `color="accentWatermelon"`. */
    textAccentWatermelon: {
      color: theme.custom.palette.colorAccentWatermelon,
      '&:hover': {
        backgroundColor: fade(theme.custom.palette.colorAccentWatermelon, theme.palette.action.hoverOpacity),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: 'transparent',
        },
      },
    },
    /* Styles applied to the root element if `variant="outlined"` and `color="lightBlue"`. */
    outlinedAccentWatermelon: {
      color: theme.custom.palette.colorSupportSteel,
      border: `1px solid ${theme.custom.palette.colorAccentWatermelon}`,
      '&:hover': {
        border: `1px solid ${theme.custom.palette.colorAccentWatermelon}`,
        backgroundColor: fade(theme.custom.palette.colorAccentWatermelon, theme.palette.action.hoverOpacity),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: 'transparent',
        },
      },
    },
    /* Styles applied to the root element if `variant="contained"` and `color="lightBlue"`. */
    containedAccentWatermelon: {
      color: theme.palette.error.contrastText,
      border: `1px solid ${theme.custom.palette.colorAccentWatermelon}`,
      backgroundColor: theme.custom.palette.colorAccentWatermelon,
      boxShadow: 'none !important',
      '&:hover': {
        backgroundColor: theme.custom.palette.colorAccentWatermelon,
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: theme.custom.palette.colorAccentWatermelon,
        },
      },
    },
    /* Styles applied to the root element if `variant="text"` and `color="accentWatermelon"`. */
    textSupportSteel: {
      color: theme.custom.palette.colorSupportSteel,
      '&:hover': {
        backgroundColor: fade(theme.custom.palette.colorSupportSteel, theme.palette.action.hoverOpacity),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: 'transparent',
        },
      },
    },
    /* Styles applied to the root element if `variant="outlined"` and `color="lightBlue"`. */
    outlinedSupportSteel: {
      color: theme.custom.palette.colorSupportSteel,
      border: `1px solid ${theme.custom.palette.colorSupportSteel}`,
      '&:hover': {
        border: `1px solid ${theme.custom.palette.colorSupportSteel}`,
        backgroundColor: fade(theme.custom.palette.colorSupportSteel, theme.palette.action.hoverOpacity),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: 'transparent',
        },
      },
    },
    /* Styles applied to the root element if `variant="contained"` and `color="lightBlue"`. */
    containedSupportSteel: {
      color: theme.palette.error.contrastText,
      border: `1px solid ${theme.custom.palette.colorSupportSteel}`,
      backgroundColor: theme.custom.palette.colorSupportSteel,
      boxShadow: 'none !important',
      '&:hover': {
        backgroundColor: theme.custom.palette.colorSupportSteel,
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: theme.custom.palette.colorSupportSteel,
        },
      },
    },
    labelUnderline: {
      textDecoration: 'underline',
    },
    rounded: {
      borderRadius: 4,
    },
    textUnderline: {
      '&:hover': {
        background: 'none',
      },
    },
    gap: {
      flex: 1,
    },
  });

export interface ButtonProps
  extends Omit<MuiButtonProps, 'classes' | 'variant' | 'size' | 'color'>,
    WithStyles<typeof styles> {
  variant?: MuiButtonProps['variant'] | 'select';
  color?:
    | MuiButtonProps['color']
    | 'tertiary'
    | 'error'
    | 'success'
    | 'lightBlue'
    | 'accentWatermelon'
    | 'supportSteel';
  size?: MuiButtonProps['size'] | 'xsmall' | 'xlarge';
  icon?: any;
  rounded?: boolean;
  underline?: boolean;
}

const Button: React.FC<ButtonProps> = ({
  classes: classesProp,
  children,
  variant: variantProp,
  color: colorProp,
  size: sizeProp,
  rounded,
  icon,
  underline,
  ...rest
}) => {
  const variant = useMemo(() => {
    if (variantProp === 'select') {
      return 'outlined';
    }
    return variantProp || 'text';
  }, [variantProp]);

  const size = useMemo(() => {
    if (sizeProp === 'xlarge') {
      return 'large';
    }
    if (sizeProp === 'xsmall') {
      return 'small';
    }
    return sizeProp;
  }, [sizeProp]);

  const color = useMemo(() => {
    if (colorProp === 'error' || colorProp === 'success') {
      return 'default';
    }
    if (
      colorProp === 'tertiary' ||
      colorProp === 'lightBlue' ||
      colorProp === 'accentWatermelon' ||
      colorProp === 'supportSteel'
    ) {
      return 'primary';
    }
    return colorProp;
  }, [colorProp]);

  const classes = useMemo(() => {
    const text = variant === 'text';
    const outlined = variant === 'outlined';
    const contained = variant === 'contained';
    const tertiary = colorProp === 'tertiary';
    const error = colorProp === 'error';
    const success = colorProp === 'success';
    const lightBlue = colorProp === 'lightBlue';
    const accentWatermelon = colorProp === 'accentWatermelon';
    const supportSteel = colorProp === 'supportSteel';

    return {
      root: classnames({
        [classesProp.withIcon]: icon,
        [classesProp.variantSelect]: variantProp === 'select',
        [classesProp.sizeExtraSmall]: sizeProp === 'xsmall',
        [classesProp.sizeLarge]: sizeProp === 'large',
        [classesProp.sizeExtraLarge]: sizeProp === 'xlarge',
        [classesProp.outlined]: outlined,
        [classesProp.textTertiary]: text && tertiary,
        [classesProp.textError]: text && error,
        [classesProp.textSuccess]: text && success,
        [classesProp.textLightBlue]: text && lightBlue,
        [classesProp.textAccentWatermelon]: text && accentWatermelon,
        [classesProp.textSupportSteel]: text && supportSteel,
        [classesProp.outlinedTertiary]: outlined && tertiary,
        [classesProp.outlinedError]: outlined && error,
        [classesProp.outlinedSuccess]: outlined && success,
        [classesProp.outlinedLightBlue]: outlined && lightBlue,
        [classesProp.outlinedAccentWatermelon]: outlined && accentWatermelon,
        [classesProp.outlinedSupportSteel]: outlined && supportSteel,
        [classesProp.containedTertiary]: contained && tertiary,
        [classesProp.containedError]: contained && error,
        [classesProp.containedSuccess]: contained && success,
        [classesProp.containedLightBlue]: contained && lightBlue,
        [classesProp.containedAccentWatermelon]: contained && accentWatermelon,
        [classesProp.containedSupportSteel]: contained && supportSteel,
        [classesProp.rounded]: rounded,
      }),
      label: classnames({ [classesProp.labelUnderline]: underline }),
      text: classnames({ [classesProp.textUnderline]: underline }),
    };
  }, [
    variant,
    variantProp,
    colorProp,
    classesProp.withIcon,
    classesProp.variantSelect,
    classesProp.sizeExtraSmall,
    classesProp.sizeLarge,
    classesProp.sizeExtraLarge,
    classesProp.outlined,
    classesProp.textTertiary,
    classesProp.textError,
    classesProp.textSuccess,
    classesProp.textLightBlue,
    classesProp.textAccentWatermelon,
    classesProp.textSupportSteel,
    classesProp.outlinedTertiary,
    classesProp.outlinedError,
    classesProp.outlinedSuccess,
    classesProp.outlinedLightBlue,
    classesProp.outlinedAccentWatermelon,
    classesProp.outlinedSupportSteel,
    classesProp.containedTertiary,
    classesProp.containedError,
    classesProp.containedSuccess,
    classesProp.containedLightBlue,
    classesProp.containedAccentWatermelon,
    classesProp.containedSupportSteel,
    classesProp.rounded,
    classesProp.labelUnderline,
    classesProp.textUnderline,
    icon,
    sizeProp,
    rounded,
    underline,
  ]);

  return (
    <MuiButton classes={classes} variant={variant} color={color} size={size} disableRipple={underline} {...rest}>
      {icon}
      {children}
      {variantProp === 'select' && (
        <>
          <div className={classesProp.gap} />
          <KeyboardArrowDownIcon />
        </>
      )}
    </MuiButton>
  );
};

export default withStyles(styles, { name: 'LumaButton' })(Button);
